/**
 * Permission Context
 *
 * Provides user role and permission checking throughout the admin panel.
 * Fetches user profile from JWT token and caches permissions.
 *
 * Part of Phase 4: Role-Based Access Control
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getStoredToken } from '../services/tokenRefreshService';
import { authInstance } from '../services/httpClient';
import Logger from '../utils/Logger';
import { allowsPageFor, allowsSectionFor, pageKeyForPath } from '../config/adminPages';

/**
 * Role hierarchy for authorization
 *
 * Staff roles: owner, admin, marketing, manager, staff
 * Customer role: customer (regular app users)
 */
export type UserRole = 'owner' | 'admin' | 'staff' | 'tablet' | 'customer';

/**
 * Which of a user's roles the admin panel should act as.
 *
 * Roles are a SET: an admin who also shops in the app carries
 * ['customer', 'admin'], and taking roles[0] made the panel treat them as a
 * customer — no menu, every page refused. Pick the most privileged staff role;
 * `customer` only when it is all there is.
 */
const STAFF_ROLE_PRIORITY: UserRole[] = ['owner', 'admin', 'staff'];
export const pickStaffRole = (roles: unknown): UserRole | null => {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  const found = STAFF_ROLE_PRIORITY.find((r) => roles.includes(r));
  if (found) return found;
  return roles.includes('customer') ? 'customer' : (roles[0] as UserRole);
};

/**
 * Permission context state
 */
interface PermissionContextState {
  role: UserRole | null;
  loading: boolean;
  hasRole: (requiredRole: UserRole | UserRole[]) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  /** Per-user page allow-list (`user.pagePermissions`); null = the role's default pages. */
  pagePermissions: string[] | null;
  /** May this user see the page with this registry key? (null list → yes) */
  canSeePage: (pageKey: string) => boolean;
  /** May this user see any page of this section? */
  canSeeSection: (sectionKey: string) => boolean;
  /** May this user view this route? Paths outside the registry are never gated. */
  canSeePath: (pathname: string) => boolean;
}

const PermissionContext = createContext<PermissionContextState | undefined>(undefined);

/**
 * Read the first role from localStorage (synchronous, fast).
 * Returns null if not found or invalid.
 */
const PAGE_PERMISSIONS_KEY = 'userPagePermissions';
const readPagePermissionsFromStorage = (): string[] | null => {
  try {
    const stored = localStorage.getItem(PAGE_PERMISSIONS_KEY);
    if (!stored) return null;
    const list = JSON.parse(stored);
    return Array.isArray(list) && list.length > 0 ? list.filter((k) => typeof k === 'string') : null;
  } catch { /* corrupted JSON */ }
  return null;
};

const readRoleFromStorage = (): UserRole | null => {
  try {
    const stored = localStorage.getItem('userRoles');
    if (!stored) return null;
    const roles = JSON.parse(stored);
    return pickStaffRole(roles);
  } catch { /* corrupted JSON */ }
  return null;
};

/**
 * Permission Provider Component
 *
 * RESILIENCE: Reads role from localStorage on mount AND re-reads whenever
 * the stored token changes (e.g., after refresh). This prevents the stale-role
 * bug where the useEffect ran once, the background /users/profile call failed,
 * and the role stayed null until a full page reload.
 */
export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const [role, setRole] = useState<UserRole | null>(() => readRoleFromStorage());
  const [pagePermissions, setPagePermissions] = useState<string[] | null>(() => readPagePermissionsFromStorage());
  const [loading, setLoading] = useState(!readRoleFromStorage());

  /**
   * Fetch fresh roles from the backend and update state + localStorage.
   * Called on mount and whenever we detect a token change.
   */
  const refreshRole = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      Logger.warn('[PermissionContext] No token found in storage');
      setLoading(false);
      return;
    }

    try {
      const response = await authInstance.get('/users/profile', { timeout: 5000 });
      const userData = response.data;
      const freshRole = pickStaffRole(userData.roles);
      if (freshRole) {
        setRole(freshRole);
        // Keep localStorage in sync so next mount is instant
        localStorage.setItem('userRoles', JSON.stringify(userData.roles));
        Logger.log('[PermissionContext] Role refreshed from API:', freshRole);
      }
      // Page allow-list rides on the same profile call. Always take the server's
      // word for it — clearing a restriction must reach the panel too.
      const freshPages = Array.isArray(userData.pagePermissions) && userData.pagePermissions.length > 0
        ? (userData.pagePermissions as string[])
        : null;
      setPagePermissions(freshPages);
      if (freshPages) localStorage.setItem(PAGE_PERMISSIONS_KEY, JSON.stringify(freshPages));
      else localStorage.removeItem(PAGE_PERMISSIONS_KEY);
    } catch (error: any) {
      Logger.warn('[PermissionContext] Profile fetch failed:', error.message);
      // Don't clear role — keep whatever we read from localStorage
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial role comes from localStorage (set synchronously in useState).
    // Fire a background API call to verify / refresh it.
    refreshRole();

    // Listen for storage changes from other tabs (e.g., login in another tab)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue) {
        // Token changed (refresh or new login) — re-read roles
        const storedRole = readRoleFromStorage();
        if (storedRole) setRole(storedRole);
        refreshRole();
      }
      if (e.key === 'userRoles' && e.newValue) {
        const storedRole = readRoleFromStorage();
        if (storedRole) setRole(storedRole);
      }
    };
    window.addEventListener('storage', onStorage);

    // Poll localStorage for token changes within the same tab.
    // StorageEvent only fires for CROSS-TAB changes — same-tab writes to
    // localStorage don't trigger it. This catches token refreshes.
    // 30s interval is sufficient — role changes are rare and the token refresh
    // itself doesn't change roles. Previously 5s caused excessive API calls.
    let lastToken = getStoredToken();
    const interval = setInterval(() => {
      const currentToken = getStoredToken();
      if (currentToken && currentToken !== lastToken) {
        lastToken = currentToken;
        const storedRole = readRoleFromStorage();
        if (storedRole) setRole(storedRole);
        // Don't call refreshRole() on every token refresh — roles rarely change.
        // The localStorage read above is sufficient for same-session consistency.
      }
    }, 30000);

    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [refreshRole]);

  /**
   * Check if user has a specific role
   */
  const hasRole = (requiredRole: UserRole | UserRole[]): boolean => {
    if (!role) return false;

    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role);
    }

    return role === requiredRole;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!role) return false;
    return roles.includes(role);
  };

  // Staff: only what is ticked, nothing when nothing is (adminPages.isTicksOnlyRole)
  const canSeePage = (pageKey: string): boolean => allowsPageFor(role, pagePermissions, pageKey);
  const canSeeSection = (sectionKey: string): boolean => allowsSectionFor(role, pagePermissions, sectionKey);
  const canSeePath = (pathname: string): boolean => {
    const key = pageKeyForPath(pathname);
    return key === null || allowsPageFor(role, pagePermissions, key);
  };

  const value: PermissionContextState = {
    role,
    loading,
    hasRole,
    hasAnyRole,
    pagePermissions,
    canSeePage,
    canSeeSection,
    canSeePath,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to access permission context
 */
export const usePermissions = (): PermissionContextState => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Role-based visibility helper
 */
export const canViewResource = (resource: string, userRole: UserRole | null): boolean => {
  if (!userRole) return false;

  // Owner can see everything
  if (userRole === 'owner') return true;

  // Admin can see most things
  if (userRole === 'admin') {
    const restrictedResources: string[] = [];
    return !restrictedResources.includes(resource); // Admin can see all current resources
  }

  // Staff: everything a ticked page may need. WHICH pages they see is the per-user
  // page allow-list (adminPages.ts canSeePath / canSeeSection), not the role.
  if (userRole === 'staff') {
    return [
      'orders',
      'command-center',
      'store-settings',
      'products',
      'categories',
      'parcels',
      'banners',
      'banner-items',
    ].includes(resource);
  }

  return false;
};
